/** @jsx jsx */
import React, { forwardRef } from 'react';
import { css, styled, jsx } from '@compiled/react';
import type { PopupComponentProps } from '@atlaskit/popup';
import { layers } from '@atlaskit/theme/constants';
import { token } from '@atlaskit/tokens';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils/src/utils/component-with-condition/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { PICKER_DROPDOWN_WIDTH } from '../../common/ui/styled.tsx';

const getPopupComponent = (width?: number) =>
	forwardRef<HTMLDivElement, PopupComponentProps>((props, ref) => (
		<PopupComponentContainer {...props} ref={ref} width={width} />
	));

export const DefaultPopupComponent = getPopupComponent();

export const FixedWidthPopupComponent = getPopupComponent(PICKER_DROPDOWN_WIDTH);

// Workaround for https://github.com/atlassian-labs/compiled/issues/1200
const layerValue = layers.layer;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PopupComponentContainerOld = styled.div<{ width?: number }>(
	{
		backgroundColor: token('elevation.surface.overlay'),
		// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
		borderRadius: token('radius.small', '3px'),
		boxShadow: token('elevation.shadow.overlay'),
		boxSizing: 'border-box',
		display: 'flex',
		flexDirection: 'column',
		flex: '1 1 auto',
		overflow: 'visible',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		zIndex: layerValue(),
		'&:focus': {
			outline: 'none',
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles, @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	({ width }) => (width ? css({ width: `${width}px` }) : undefined),
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PopupComponentContainerNew = styled.div<{ width?: number }>(
	{
		backgroundColor: token('elevation.surface.overlay'),
		// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
		borderRadius: token('radius.small', '3px'),
		boxShadow: token('elevation.shadow.overlay'),
		boxSizing: 'border-box',
		display: 'flex',
		flexDirection: 'column',
		flex: '1 1 auto',
		overflow: 'visible',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		zIndex: layerValue(),
		'&:focus': {
			outline: 'none',
		},
		// this is the only change from the old styles for slv-117-nin-column-picker-style-updates
		paddingTop: token('space.150'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles, @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	({ width }) => (width ? css({ width: `${width}px` }) : undefined),
);

const PopupComponentContainer = componentWithCondition(
	() => fg('slv-117-nin-column-picker-style-updates'),
	PopupComponentContainerNew,
	PopupComponentContainerOld,
);
